import { jsPDF } from 'jspdf';
import { Book } from '../types/index.js';

function sanitizePdfText(input: any, maxLength?: number, fallback = '-'): string {
  if (input === null || input === undefined) return fallback;
  let str = String(input);
  try {
    str = str.normalize('NFC');
  } catch {
    // ignore
  }
  str = str
    .replace(/<[^>]*>/g, ' ')
    .replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g, '')
    .replace(/[\u200B-\u200D\uFEFF\u202A-\u202E\u2066-\u2069]/g, '')
    .replace(/[\r\n\t]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  if (!str) return fallback;
  if (maxLength && str.length > maxLength) {
    return str.slice(0, maxLength).trim();
  }
  return str;
}

export interface DashboardPdfOptions {
  stats: {
    summary: {
      totalBooks: number;
      totalApproved: number;
      totalPending: number;
      totalDrafts: number;
      totalArchived: number;
      totalScans: number;
      totalUsers: number;
    };
    statusCounts: {
      disetujui: number;
      diajukan: number;
      draft: number;
      diarsipkan: number;
    };
    categoryBreakdown: Array<{ name: string; count: number; percentage: number }>;
    scanTrends: Array<{ date: string; count: number }>;
    topScanned: Array<{ title: string; uibn: string; scanCount: number; category?: string }>;
    rolesCount?: {
      admin?: number;
      penerbit?: number;
      penulis?: number;
      pembaca?: number;
    };
  };
  user?: {
    name?: string;
    role?: string;
    organization?: string | null;
    email?: string;
  } | null;
  filteredBooks?: Book[];
  dateRangeLabel?: string;
}

export async function generateDashboardPdfReport(options: DashboardPdfOptions): Promise<void> {
  const { stats, user, filteredBooks = [], dateRangeLabel = 'Semua Waktu' } = options;

  const doc = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4',
    compress: true,
  });

  const pageWidth = 210;
  const pageHeight = 297;
  const margin = 14;
  const contentWidth = pageWidth - margin * 2; // 182mm

  let currentY = margin;

  const checkPageBreak = (neededHeight: number) => {
    if (currentY + neededHeight > pageHeight - margin - 12) {
      doc.addPage();
      currentY = margin + 10;
      drawHeaderBanner(false);
    }
  };

  const drawHeaderBanner = (isFirstPage = true) => {
    if (isFirstPage) {
      // Top Navy Header Box
      doc.setFillColor(30, 27, 75); // #1e1b4b
      doc.rect(margin, currentY, contentWidth, 20, 'F');

      // Top Gold Accent Line
      doc.setFillColor(245, 158, 11); // #f59e0b
      doc.rect(margin, currentY, contentWidth, 1.5, 'F');

      // Left Title Text
      doc.setTextColor(255, 255, 255);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10);
      doc.text('REKAPITULASI INVENTARIS BUKU & LOG UIBN', margin + 4, currentY + 7);

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(7.5);
      doc.setTextColor(224, 231, 255);
      doc.text('Sistem Katalog & Verifikasi Resmi Barcode 1D / QR Code 2D', margin + 4, currentY + 13);

      // Right Metadata Text
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(6.5);
      doc.setTextColor(253, 230, 138);
      doc.text('DOKUMEN RESMI TERCETAK AUTOMATIS', margin + contentWidth - 4, currentY + 7, { align: 'right' });

      doc.setFont('helvetica', 'normal');
      doc.setTextColor(226, 232, 240);
      const todayStr = new Date().toLocaleDateString('id-ID', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
      doc.text(`Tanggal Cetak: ${todayStr}`, margin + contentWidth - 4, currentY + 13, { align: 'right' });

      currentY += 24;

      // Report Main Heading Block
      doc.setFillColor(248, 250, 252);
      doc.setDrawColor(226, 232, 240);
      doc.roundedRect(margin, currentY, contentWidth, 18, 2, 2, 'FD');

      doc.setTextColor(15, 23, 42);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10.5);
      doc.text('LAPORAN RINGKASAN INVENTARIS BUKU & AKTIVITAS PEMINDAIAN', margin + 4, currentY + 6);

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(7.5);
      doc.setTextColor(71, 85, 105);

      const userRoleStr = user?.role === 'admin'
        ? 'Pengawasan Administrator UIBN Nasional'
        : user?.role === 'penerbit'
        ? `Katalog Penerbit: ${user.organization || user.name}`
        : user?.role === 'penulis'
        ? `Katalog Penulis: ${user.name}`
        : 'Publik / Terautentikasi';

      doc.text(`Cakupan Pengguna: ${userRoleStr}  |  Periode Data: ${dateRangeLabel}`, margin + 4, currentY + 12);

      currentY += 22;
    } else {
      // Sub-page running header
      doc.setFillColor(241, 245, 249);
      doc.rect(margin, margin - 4, contentWidth, 8, 'F');
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(7);
      doc.setTextColor(71, 85, 105);
      doc.text('LAPORAN INVENTARIS & PEMINDAIAN UIBN (Lanjutan)', margin + 3, margin + 1);
      doc.setFont('helvetica', 'normal');
      doc.text(`Cetak: ${new Date().toLocaleDateString('id-ID')}`, margin + contentWidth - 3, margin + 1, { align: 'right' });
      doc.setDrawColor(203, 213, 225);
      doc.line(margin, margin + 5, margin + contentWidth, margin + 5);
    }
  };

  // Start Page 1
  drawHeaderBanner(true);

  // SECTION 1: Summary KPI Metrics (4 Cards Grid)
  checkPageBreak(30);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(30, 41, 59);
  doc.text('1. RINGKASAN METRIK UTAMA (SUMMARY METRICS)', margin, currentY);
  currentY += 4;

  const cardWidth = (contentWidth - 9) / 4;
  const cardHeight = 18;

  const metrics = [
    { label: 'Total Buku', value: stats.summary.totalBooks.toString(), sub: `${stats.summary.totalApproved} Aktif`, bgR: 238, bgG: 242, bgB: 255, strokeR: 199, strokeG: 210, strokeB: 254 },
    { label: 'Total Pemindaian', value: stats.summary.totalScans.toString(), sub: 'Barcode 1D & QR 2D', bgR: 236, bgG: 253, bgB: 245, strokeR: 167, strokeG: 243, strokeB: 208 },
    { label: 'Menunggu Verifikasi', value: stats.summary.totalPending.toString(), sub: 'Naskah Diajukan', bgR: 254, bgG: 243, bgB: 199, strokeR: 253, strokeG: 230, strokeB: 138 },
    { label: 'Entitas Terdaftar', value: stats.summary.totalUsers.toString(), sub: `${stats.rolesCount?.penerbit || 1} Penerbit, ${stats.rolesCount?.penulis || 1} Penulis`, bgR: 243, bgG: 232, bgB: 255, strokeR: 233, strokeG: 213, strokeB: 255 },
  ];

  metrics.forEach((m, idx) => {
    const cardX = margin + idx * (cardWidth + 3);
    doc.setFillColor(m.bgR, m.bgG, m.bgB);
    doc.setDrawColor(m.strokeR, m.strokeG, m.strokeB);
    doc.roundedRect(cardX, currentY, cardWidth, cardHeight, 1.5, 1.5, 'FD');

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7);
    doc.setTextColor(71, 85, 105);
    doc.text(m.label, cardX + 3, currentY + 4.5);

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.setTextColor(15, 23, 42);
    doc.text(m.value, cardX + 3, currentY + 11);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(6.5);
    doc.setTextColor(100, 116, 139);
    doc.text(m.sub, cardX + 3, currentY + 15.5);
  });

  currentY += cardHeight + 8;

  // SECTION 2: Status Breakdown & Category Breakdown Side-by-Side
  checkPageBreak(50);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(30, 41, 59);
  doc.text('2. DISTRIBUSI STATUS BUKU & SEBARAN KATEGORI', margin, currentY);
  currentY += 4;

  const halfWidth = (contentWidth - 6) / 2;

  // Left Box: Status Breakdown Table
  const leftX = margin;
  doc.setFillColor(255, 255, 255);
  doc.setDrawColor(226, 232, 240);
  doc.roundedRect(leftX, currentY, halfWidth, 42, 2, 2, 'FD');

  doc.setFillColor(248, 250, 252);
  doc.rect(leftX, currentY, halfWidth, 7, 'F');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.setTextColor(30, 41, 59);
  doc.text('Status Kesiapan Publikasi', leftX + 3, currentY + 4.8);

  const statuses = [
    { label: 'Disetujui / Aktif Resmi', count: stats.statusCounts.disetujui, colorR: 16, colorG: 185, colorB: 129 },
    { label: 'Diajukan (Menunggu Review)', count: stats.statusCounts.diajukan, colorR: 245, colorG: 158, colorB: 11 },
    { label: 'Draft Naskah', count: stats.statusCounts.draft, colorR: 148, colorG: 163, colorB: 184 },
    { label: 'Diarsipkan', count: stats.statusCounts.diarsipkan, colorR: 120, colorG: 113, colorB: 108 },
  ];

  let statusY = currentY + 12;
  const totalB = stats.summary.totalBooks || 1;

  statuses.forEach((st) => {
    const pct = Math.round((st.count / totalB) * 100);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.5);
    doc.setTextColor(51, 65, 85);
    doc.text(st.label, leftX + 3, statusY);

    doc.setFont('helvetica', 'bold');
    doc.text(`${st.count} (${pct}%)`, leftX + halfWidth - 3, statusY, { align: 'right' });

    // Mini bar
    doc.setFillColor(241, 245, 249);
    doc.rect(leftX + 3, statusY + 1.5, halfWidth - 6, 2, 'F');
    const barW = Math.max(1, Math.round(((halfWidth - 6) * pct) / 100));
    doc.setFillColor(st.colorR, st.colorG, st.colorB);
    doc.rect(leftX + 3, statusY + 1.5, barW, 2, 'F');

    statusY += 7.5;
  });

  // Right Box: Category Breakdown
  const rightX = margin + halfWidth + 6;
  doc.setFillColor(255, 255, 255);
  doc.setDrawColor(226, 232, 240);
  doc.roundedRect(rightX, currentY, halfWidth, 42, 2, 2, 'FD');

  doc.setFillColor(248, 250, 252);
  doc.rect(rightX, currentY, halfWidth, 7, 'F');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.setTextColor(30, 41, 59);
  doc.text('Kategori Judul Populer', rightX + 3, currentY + 4.8);

  let catY = currentY + 12;
  const topCategories = (stats.categoryBreakdown || []).slice(0, 4);

  if (topCategories.length === 0) {
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(7.5);
    doc.setTextColor(148, 163, 184);
    doc.text('Belum ada data kategori.', rightX + 3, catY);
  } else {
    topCategories.forEach((cat) => {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(7.5);
      doc.setTextColor(51, 65, 85);
      const catNameFormatted = cat.name.length > 20 ? cat.name.slice(0, 18) + '...' : cat.name;
      doc.text(catNameFormatted, rightX + 3, catY);

      doc.setFont('helvetica', 'bold');
      doc.text(`${cat.count} buku (${cat.percentage}%)`, rightX + halfWidth - 3, catY, { align: 'right' });

      doc.setFillColor(224, 231, 255);
      doc.rect(rightX + 3, catY + 1.5, halfWidth - 6, 2, 'F');
      const barW = Math.max(1, Math.round(((halfWidth - 6) * cat.percentage) / 100));
      doc.setFillColor(79, 70, 229);
      doc.rect(rightX + 3, catY + 1.5, barW, 2, 'F');

      catY += 7.5;
    });
  }

  currentY += 48;

  // SECTION 3: 7-Day Scan Activity Trends
  checkPageBreak(35);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(30, 41, 59);
  doc.text('3. LOG TREN PEMINDAIAN BARCODE & QR CODE 7 HARI TERAKHIR', margin, currentY);
  currentY += 4;

  const trends = stats.scanTrends || [];

  doc.setFillColor(255, 255, 255);
  doc.setDrawColor(226, 232, 240);
  doc.roundedRect(margin, currentY, contentWidth, 26, 2, 2, 'FD');

  const numTrends = Math.max(1, trends.length);
  const trendColW = contentWidth / numTrends;
  const maxTrendVal = Math.max(...trends.map((t) => t.count), 1);

  trends.forEach((tr, i) => {
    const colX = margin + i * trendColW;

    // Bar drawing
    const maxBarH = 12;
    const barH = Math.max(1, Math.round((tr.count / maxTrendVal) * maxBarH));
    const barY = currentY + 18 - barH;

    doc.setFillColor(99, 102, 241);
    doc.rect(colX + trendColW / 2 - 3, barY, 6, barH, 'F');

    // Count label above bar
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(6.5);
    doc.setTextColor(30, 41, 59);
    doc.text(tr.count.toString(), colX + trendColW / 2, barY - 1.5, { align: 'center' });

    // Date label below
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(6);
    doc.setTextColor(100, 116, 139);
    const shortDate = tr.date ? tr.date.slice(5) : '-';
    doc.text(shortDate, colX + trendColW / 2, currentY + 23, { align: 'center' });
  });

  currentY += 32;

  // SECTION 4: Top Scanned Books Leaderboard
  checkPageBreak(45);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(30, 41, 59);
  doc.text('4. BUKU PALING SERING DIPINDAI (TOP SCANNED LEADERBOARD)', margin, currentY);
  currentY += 4;

  // Table Header
  doc.setFillColor(30, 27, 75);
  doc.rect(margin, currentY, contentWidth, 6, 'F');

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7);
  doc.setTextColor(255, 255, 255);

  doc.text('NO', margin + 3, currentY + 4);
  doc.text('JUDUL BUKU', margin + 12, currentY + 4);
  doc.text('NOMOR UIBN', margin + 110, currentY + 4);
  doc.text('KATEGORI', margin + 145, currentY + 4);
  doc.text('PEMINDAIAN', margin + contentWidth - 3, currentY + 4, { align: 'right' });

  currentY += 6;

  const topScanned = stats.topScanned || [];

  if (topScanned.length === 0) {
    doc.setFillColor(248, 250, 252);
    doc.rect(margin, currentY, contentWidth, 8, 'F');
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(7.5);
    doc.setTextColor(148, 163, 184);
    doc.text('Belum ada data pemindaian tercatat.', margin + 4, currentY + 5);
    currentY += 8;
  } else {
    topScanned.slice(0, 5).forEach((b, idx) => {
      checkPageBreak(7);

      const rowBg = idx % 2 === 0 ? 255 : 248;
      doc.setFillColor(rowBg, rowBg, rowBg === 248 ? 250 : 255);
      doc.rect(margin, currentY, contentWidth, 6.5, 'F');

      doc.setDrawColor(241, 245, 249);
      doc.line(margin, currentY + 6.5, margin + contentWidth, currentY + 6.5);

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(7);
      doc.setTextColor(30, 41, 59);
      doc.text((idx + 1).toString(), margin + 3, currentY + 4.2);

      doc.setFont('helvetica', 'semibold');
      const trimmedTitle = b.title.length > 52 ? b.title.slice(0, 50) + '...' : b.title;
      doc.text(trimmedTitle, margin + 12, currentY + 4.2);

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(6.5);
      doc.setTextColor(71, 85, 105);
      doc.text(b.uibn || '-', margin + 110, currentY + 4.2);
      doc.text(b.category || 'Umum', margin + 145, currentY + 4.2);

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(7);
      doc.setTextColor(79, 70, 229);
      doc.text(`${b.scanCount} scan`, margin + contentWidth - 3, currentY + 4.2, { align: 'right' });

      currentY += 6.5;
    });
  }

  currentY += 6;

  // SECTION 5: Filtered Book Inventory Summary List (If provided)
  if (filteredBooks && filteredBooks.length > 0) {
    checkPageBreak(50);

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(30, 41, 59);
    doc.text(`5. DAFTAR SAMPEL INVENTARIS BUKU TERSARING (${filteredBooks.length} ITEMS)`, margin, currentY);
    currentY += 4;

    // Table Header
    doc.setFillColor(51, 65, 85);
    doc.rect(margin, currentY, contentWidth, 6, 'F');

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7);
    doc.setTextColor(255, 255, 255);

    doc.text('NO', margin + 3, currentY + 4);
    doc.text('JUDUL BUKU', margin + 12, currentY + 4);
    doc.text('NOMOR UIBN', margin + 90, currentY + 4);
    doc.text('PENERBIT / PENULIS', margin + 125, currentY + 4);
    doc.text('STATUS', margin + 165, currentY + 4);

    currentY += 6;

    filteredBooks.slice(0, 15).forEach((b, idx) => {
      checkPageBreak(7);

      const rowBg = idx % 2 === 0 ? 255 : 248;
      doc.setFillColor(rowBg, rowBg, rowBg === 248 ? 250 : 255);
      doc.rect(margin, currentY, contentWidth, 6.5, 'F');

      doc.setDrawColor(241, 245, 249);
      doc.line(margin, currentY + 6.5, margin + contentWidth, currentY + 6.5);

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(7);
      doc.setTextColor(30, 41, 59);
      doc.text((idx + 1).toString(), margin + 3, currentY + 4.2);

      doc.setFont('helvetica', 'semibold');
      const trimmedTitle = b.title.length > 42 ? b.title.slice(0, 40) + '...' : b.title;
      doc.text(trimmedTitle, margin + 12, currentY + 4.2);

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(6.5);
      doc.setTextColor(71, 85, 105);
      doc.text(b.uibn || '-', margin + 90, currentY + 4.2);

      const publisherOrAuthor = b.publisherName || b.authorName || '-';
      const trimmedEntity = publisherOrAuthor.length > 22 ? publisherOrAuthor.slice(0, 20) + '..' : publisherOrAuthor;
      doc.text(trimmedEntity, margin + 125, currentY + 4.2);

      const statusText = b.status === 'disetujui' ? 'Disetujui' : b.status === 'diajukan' ? 'Diajukan' : b.status === 'draft' ? 'Draft' : 'Arsip';
      doc.setFont('helvetica', 'bold');
      if (b.status === 'disetujui') doc.setTextColor(16, 185, 129);
      else if (b.status === 'diajukan') doc.setTextColor(245, 158, 11);
      else doc.setTextColor(100, 116, 139);

      doc.text(statusText, margin + 165, currentY + 4.2);

      currentY += 6.5;
    });

    if (filteredBooks.length > 15) {
      currentY += 2;
      doc.setFont('helvetica', 'italic');
      doc.setFontSize(6.5);
      doc.setTextColor(100, 116, 139);
      doc.text(`* Dan ${filteredBooks.length - 15} judul naskah buku lainnya tersaring dalam sistem.`, margin, currentY + 2);
      currentY += 4;
    }

    currentY += 6;
  }

  // FOOTER & STAMP (on current or last page)
  checkPageBreak(28);

  currentY += 2;
  doc.setDrawColor(203, 213, 225);
  doc.line(margin, currentY, margin + contentWidth, currentY);
  currentY += 4;

  // Security Stamp Box
  doc.setFillColor(248, 250, 252);
  doc.setDrawColor(203, 213, 225);
  doc.roundedRect(margin, currentY, contentWidth, 18, 1.5, 1.5, 'FD');

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7);
  doc.setTextColor(30, 41, 59);
  doc.text('PENGESAHAN OTENTIKASI SISTEM UIBN NASIONAL', margin + 3, currentY + 4.5);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(6.5);
  doc.setTextColor(100, 116, 139);
  doc.text(
    'Dokumen rekapitulasi statistik ini dihasilkan secara otomatis oleh infrastruktur terdesentralisasi UIBN. Sah sebagai dokumen laporan resmi.',
    margin + 3,
    currentY + 8.5
  );

  // Hash signature string
  const hashSim = Math.random().toString(36).substring(2, 12).toUpperCase();
  const certCode = `SHA256:UIBN-RPT-${new Date().getFullYear()}-${hashSim}`;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(6);
  doc.setTextColor(79, 70, 229);
  doc.text(`KODE VALIDASI KRIPTOGRAFI: ${certCode}`, margin + 3, currentY + 13.5);

  // Add Page Numbers to all pages
  const totalPages = doc.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(6.5);
    doc.setTextColor(148, 163, 184);
    doc.text(`Halaman ${i} dari ${totalPages}`, margin + contentWidth, pageHeight - 6, { align: 'right' });
    doc.text('Sistem UIBN Indonesia © 2026 - Laporan Resmi Tergenerasi', margin, pageHeight - 6);
  }

  // Trigger Save PDF
  const cleanDate = new Date().toISOString().slice(0, 10);
  const fileName = `Laporan_UIBN_Inventaris_Pemindaian_${cleanDate}.pdf`;
  doc.save(fileName);
}
